import { Component, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import {
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";

// Services
import { StoreUserService } from '@app/core/state/store-user.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { GetUserService } from "../../../data/user/get-user.service";
import { GetUserAndBelongsService } from "../../../data/user/get-user-and-belongs.service";
import { UpdateUserService } from "../../../data/user/update-user.service";

import { finalize } from "rxjs/operators";
import dayjs from "dayjs";
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { USER_DETAILS_I18N } from "./user-details.i18n";

@Component({
  selector: "app-user-details",
  templateUrl: "./user-details.component.html",
  styleUrls: ["./user-details.component.scss"],
  imports: SHARED_IMPORTS,
})
export class UserDetailsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = USER_DETAILS_I18N;

  // Loading state for skeleton - true initially to show skeleton until data is ready
  isLoading = signal(true);
  isSending = signal(false);

  public validId: boolean = false;

  public mode = Mode.disp;
  Mode = Mode;

  userFormGroup: FormGroup;
  sexOptions = [
    { display: this.i18n.sex.male, value: "male" },
    { display: this.i18n.sex.female, value: "female" },
    { display: this.i18n.sex.other, value: "other" },
  ];

  public userData: UserData;

  private defaultAccountData: UserData = {
    userId: "",
    code: "",
    lastName: "",
    firstName: "",
    birthDate: "",
    sex: "",
    accountId: "",
    contractStatus: "",
    contractStartAt: "--/--/--",
    contractEndAt: "--/--/--",
    lastModifiedAt: "--/--/--",
    lastModifiedBy: "--",
  };

  accountStatus = [
    { value: "OPEN", viewValue: this.i18n.status.active },
    { value: "CLOSE", viewValue: this.i18n.status.inactive },
  ];
  selectedStatus = "";

  // accountGroup = [
  //   { value: 'STAFF', viewValue: 'スタッフ' },
  //   { value: 'USER', viewValue: 'ユーザー' },
  // ];

  // selectedGroup = '';

  userId: string;
  sectionId: string;
  putParam;

  constructor(
    public dialogRef: MatDialogRef<UserDetailsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public _matdialog: MatDialog,
    public storeUser: StoreUserService,
    public storeAuth: StoreAuthService,
    private route: ActivatedRoute,
    public snackBar: MatSnackBar,
    private getUserService: GetUserService,
    private getUserAndBelongsService: GetUserAndBelongsService,
    private updateUserService: UpdateUserService,
    private common: DatabaseCacheService,
    private router: Router,
  ) {
    this.userData = this.defaultAccountData;
  }

  ngOnInit() {
    this.userFormGroup = new FormGroup({
      code: new FormControl("", [Validators.required]),
      lastName: new FormControl("", [Validators.required]),
      firstName: new FormControl("", [Validators.required]),
      // contractStartAt: new FormControl('', [Validators.required, Validators.pattern('^((19|2[0-9])[0-9]{2})/(0[1-9]|1[012])/(0[1-9]|[12][0-9]|3[01])$')]),
      birthDate: new FormControl("", [
        Validators.pattern(
          "^((19|2[0-9])[0-9]{2})/(0[1-9]|1[012])/(0[1-9]|[12][0-9]|3[01])$",
        ),
      ]),
      sex: new FormControl("", [Validators.pattern("^(male|female|other)$")]),
      accountId: new FormControl(""),
      contractStartAt: new FormControl(""),
      contractEndAt: new FormControl(""),
    });

    this.userFormGroup.disable();

    this.resetFields();
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.userId = this.data?.id;
      this.sectionId = this.common.getSelectedSection().sectionId;
      this.getUser();
    });
  }

  getUser() {
    this.isLoading.set(true);
    this.resetFields();

    this.getUserAndBelongsService.getUserAndBelongs(this.userId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe(
      (res) => {
        let resAny = res as any;

        let userId = resAny.user.userId;

        if (userId === "ANONYMOUS") {
          this.snackBar.open("ERROR: invalid ID", null, {
            duration: 5000,
          });
        } else {
          this.userData = {
            userId: userId,
            code: resAny.user.code ?? null,
            lastName: resAny.user.lastName ?? null,
            firstName: resAny.user.firstName ?? null,
            birthDate: dayjs(resAny.user.birthDate).isValid()
              ? dayjs(resAny.user.birthDate).format("YYYY/MM/DD")
              : null,
            sex: resAny.user.sex ?? null,
            accountId: resAny.user.userId,
            contractStatus:
              resAny.belongs.contractInfo.contractStatus.toUpperCase(),
            contractStartAt: dayjs(
              resAny.belongs.contractInfo.contractStartAt,
            ).isValid()
              ? dayjs(resAny.belongs.contractInfo.contractStartAt).format(
                  "YYYY/MM/DD",
                )
              : null,
            contractEndAt: dayjs(
              resAny.belongs.contractInfo.contractEndAt,
            ).isValid()
              ? dayjs(resAny.belongs.contractInfo.contractEndAt).format(
                  "YYYY/MM/DD",
                )
              : null,
            lastModifiedAt: resAny.belongs.commonInfo.lastModifiedAt,
            lastModifiedBy: resAny.belongs.commonInfo.lastModifiedBy,
          };

          this.userFormGroup.patchValue(this.userData);

          this.selectedStatus =
            resAny.belongs.contractInfo.contractStatus.toUpperCase();
          // this.selectedGroup = convertedRes[0].permissions.userType.toUpperCase();
          this.validId = true;
        }
      },
      (error) => {
      },
    );
  }

  resetFields(): void {
    this.validId = false;
    this.selectedStatus = "";
    this.userData = this.defaultAccountData;
  }

  update() {
    this.updateUser();
  }

  updateUser() {
    this.putParam = {
      userType: "USER",
      contractStatus: this.selectedStatus,
    };
    this.isSending.set(true);
    this.updateUserService.updateUser(this.userId, this.putParam)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isSending.set(false)))
      .subscribe(
        (res) => {
          this.common.parametersUpdated.next({ type: 'usersListUpdated', sectionId: this.sectionId ?? null });
          this.snackBar.open(this.i18n.message.updateSuccess, this.i18n.button.close, { duration: 5000 });
        },
        (error) => {
          this.snackBar.open(this.i18n.message.updateError, this.i18n.button.close, { duration: 5000 });
        },
      );
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  onEditOrUploadClicked() {
    // 現在がディスプレイモードなら編集モードに切り替える
    if (this.mode == Mode.disp) {
      this.mode = Mode.input;

      this.userFormGroup.get("firstName").enable();
      this.userFormGroup.get("lastName").enable();
      this.userFormGroup.get("birthDate").enable();
      this.userFormGroup.get("sex").enable();
      this.userFormGroup.get("code").enable();

      return;
    }

    //そうでない場合はすでに編集モードなのでデータベースにアップロードする

    var postParams: Record<string, any> = {};

    postParams.firstName =
      this.userFormGroup.value.firstName != ""
        ? this.userFormGroup.value.firstName
        : null;
    postParams.lastName =
      this.userFormGroup.value.lastName != ""
        ? this.userFormGroup.value.lastName
        : null;
    postParams.code =
      this.userFormGroup.value.code != ""
        ? this.userFormGroup.value.code
        : null;
    postParams.birthDate = this.userFormGroup.value.birthDate;
    postParams.sex = this.userFormGroup.value.sex;

    this.isSending.set(true);
    this.updateUserService
      .updateUser(this.userData.userId, postParams)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.isSending.set(false)))
      .subscribe({
        next: (res) => {
          this.common.parametersUpdated.next({ type: 'usersListUpdated', sectionId: this.sectionId ?? null });
          this.snackBar.open(this.i18n.message.userUpdateSuccess, this.i18n.button.close, { duration: 3000 });
          this.mode = Mode.disp;
          this.userFormGroup.disable();
          this.getUser();
        },
        error: () => {
          this.snackBar.open(this.i18n.message.userUpdateError, this.i18n.button.close, { duration: 5000 });
        },
      });
  }
}

export enum Mode {
  disp,
  input,
}

// export interface AccountData {
//   user: {
//     userId: string,
//     code: string,
//     lastName: string,
//     firstName: string,
//     birthDate : string,
//     sex : string,
//   };
//   contractInfo: {
//     contractStatus: string,
//     contractStartAt: string,
//     contractEndAt: string,
//   };
//   commonInfo: {
//     lastModifiedAt: string,
//     lastModifiedBy: string
//   };
//   id: string;
// }

export interface UserData {
  userId: string;
  code: string;

  lastName: string;
  firstName: string;
  birthDate: string;
  sex: string;
  accountId: string;
  contractStatus: string;
  contractStartAt: string;
  contractEndAt: string;
  lastModifiedAt: string;
  lastModifiedBy: string;
}
